import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldWarning, DownloadSimple } from "@phosphor-icons/react";

export async function downloadCertPdf(certId) {
  const res = await api.get(`/purge-certificates/${certId}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  window.open(url, "_blank");
}

export function PurgeRow({ kind, entity, onDone }) {
  const [busy, setBusy] = useState(false);
  const [lastCert, setLastCert] = useState(null);
  const label = kind === "board" ? "board" : "school";
  const run = async () => {
    const typed = window.prompt(
      `PERMANENTLY delete ALL student accounts and learning data for this ${label} ("${entity.name}")?\n\n` +
      `This cannot be undone. A deletion certificate will be issued for your records.\n\n` +
      `Type the ${label}'s exact name to confirm:`);
    if (typed === null) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/${kind === "board" ? "boards" : "schools"}/${entity.id}/purge`, { confirm_name: typed });
      setLastCert(data.cert_id);
      toast.success(`${data.students_deleted} student account${data.students_deleted === 1 ? "" : "s"} purged — certificate ready`);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-[#E4E4E7]" data-testid={`purge-row-${entity.id}`}>
      <div className="flex items-center gap-1.5 text-xs text-[#000000]">
        <ShieldWarning size={15} weight="duotone" color="#B91C1C" />
        <span>Privacy: purge this {label}'s student data</span>
      </div>
      <div className="flex items-center gap-1.5">
        {lastCert && (
          <button data-testid={`purge-cert-${entity.id}`} onClick={() => downloadCertPdf(lastCert).catch(() => toast.error("Could not open certificate"))}
            className="text-xs font-semibold text-[#15803D] flex items-center gap-1">
            <DownloadSimple size={13} weight="bold" /> Certificate
          </button>
        )}
        <button data-testid={`purge-btn-${entity.id}`} disabled={busy} onClick={run}
          className="text-xs font-semibold text-[#B91C1C] border border-[#FCA5A5] rounded-lg px-2 py-1 hover:bg-[#FEF2F2] disabled:opacity-50">
          {busy ? "Purging…" : "Purge data"}
        </button>
      </div>
    </div>
  );
}

export function CertHistory() {
  const [certs, setCerts] = useState(null);
  const [open, setOpen] = useState(false);
  React.useEffect(() => {
    api.get("/purge-certificates").then(({ data }) => setCerts(data)).catch(() => setCerts([]));
  }, []);
  if (!certs || certs.length === 0) return null;
  return (
    <div className="rounded-xl border border-[#E4E4E7] bg-white p-3 mt-4" data-testid="cert-history-card">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between bg-transparent border-0 cursor-pointer p-0" data-testid="cert-history-toggle">
        <div className="flex items-center gap-2 font-display font-bold text-sm">
          <ShieldWarning size={16} weight="duotone" color="#B91C1C" /> Deletion certificates ({certs.length})
        </div>
        <span className="text-xs text-[#52525B]">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-1.5">
          {certs.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 text-xs border-t border-[#F1F1F0] pt-1.5" data-testid={`cert-row-${c.id}`}>
              <div className="min-w-0">
                <span className="font-semibold">{c.scope_name}</span>
                <span className="text-[#52525B]"> · {c.scope === "board" ? "board" : "school"} · {(c.ts || "").slice(0, 10)} · {c.students_deleted} student{c.students_deleted === 1 ? "" : "s"}</span>
              </div>
              <button data-testid={`cert-dl-${c.id}`} onClick={() => downloadCertPdf(c.id).catch(() => toast.error("Could not open certificate"))}
                className="shrink-0 text-xs font-semibold text-[#15803D] flex items-center gap-1 bg-transparent border-0 cursor-pointer">
                <DownloadSimple size={13} weight="bold" /> PDF
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SchoolPurgeCard({ user }) {
  const [busy, setBusy] = useState(false);
  const [lastCert, setLastCert] = useState(null);
  if (!user?.school_id) return null;
  const run = async () => {
    const typed = window.prompt(
      "PERMANENTLY delete ALL student accounts and learning data for your school?\n\nThis cannot be undone. " +
      "A deletion certificate will be issued for your records.\n\nType your school's exact name to confirm:");
    if (typed === null) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/schools/${user.school_id}/purge`, { confirm_name: typed });
      setLastCert(data.cert_id);
      toast.success(`${data.students_deleted} student account${data.students_deleted === 1 ? "" : "s"} purged — certificate ready`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-[#FCA5A5] bg-white p-4 mt-5" data-testid="school-purge-card">
      <div className="flex items-center gap-2 font-display font-bold mb-1">
        <ShieldWarning size={18} weight="duotone" color="#B91C1C" /> Data & Privacy
      </div>
      <p className="text-sm text-[#000000] mb-3">
        At the end of your agreement (or any time you choose), permanently delete all of your school's student accounts
        and learning records. A Certificate of Secure Data Destruction is issued for your board's records.
      </p>
      <div className="flex items-center gap-2">
        <button data-testid="school-purge-own-btn" disabled={busy} onClick={run}
          className="text-sm font-semibold text-[#B91C1C] border border-[#FCA5A5] rounded-lg px-3 py-1.5 hover:bg-[#FEF2F2] disabled:opacity-50">
          {busy ? "Purging…" : "Purge my school's student data"}
        </button>
        {lastCert && (
          <button data-testid="school-purge-own-cert" onClick={() => downloadCertPdf(lastCert).catch(() => toast.error("Could not open certificate"))}
            className="text-sm font-semibold text-[#15803D] flex items-center gap-1">
            <DownloadSimple size={15} weight="bold" /> Download certificate
          </button>
        )}
      </div>
      <CertHistory />
    </div>
  );
}
